import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { 
  CheckCircle, ArrowRight, MessageCircle, Zap, 
  FileText, Calendar, BarChart3, Code, Image, Search,
  Users, Brain, Mic, Presentation
} from "lucide-react";
import { Link } from "react-router-dom";
import Navigation from "../../components/Navigation";

const WHATSAPP_LINK = "https://wa.me/33644692901?text=Bonjour%20!%20Je%20souhaite%20une%20formation%20sur%20les%20outils%20IA%20entreprise.";

const categories = [
  {
    title: "Assistants IA conversationnels",
    color: "cyan",
    tools: [
      {
        name: "ChatGPT (OpenAI)",
        description: "L'IA conversationnelle la plus populaire. Rédaction, analyse, code, création.",
        strengths: ["Plan gratuit généreux", "Écosystème GPTs", "Multimodal (texte, image, voix)"],
        price: "Gratuit à 20€/mois"
      },
      {
        name: "Claude (Anthropic)",
        description: "IA éthique avec fenêtre de contexte massive. Idéal pour documents longs.",
        strengths: ["200 000 tokens de contexte", "Réponses nuancées", "Moins d'hallucinations"],
        price: "Gratuit à 20€/mois"
      },
      {
        name: "Gemini (Google)",
        description: "Intégré à Google Workspace. Accès à vos emails, docs, agenda.",
        strengths: ["Intégration Google native", "Recherche web en temps réel", "Gratuit avec Google One"],
        price: "Gratuit à 20€/mois"
      }
    ]
  },
  {
    title: "Productivité & Organisation",
    color: "purple",
    tools: [
      {
        name: "Notion AI",
        description: "Transforme vos notes en plans cohérents. Résume, reformule, génère.",
        strengths: ["Intégré à Notion", "Génération de contenu", "Organisation automatique"],
        price: "10€/mois"
      },
      {
        name: "Microsoft 365 Copilot",
        description: "IA intégrée à Word, Excel, PowerPoint, Outlook, Teams.",
        strengths: ["Intégration Microsoft native", "10h gagnées/mois en moyenne", "Sécurité entreprise"],
        price: "30€/utilisateur/mois"
      },
      {
        name: "Otter.ai",
        description: "Transcription automatique de réunions avec résumés intelligents.",
        strengths: ["Transcription temps réel", "Résumés automatiques", "Intégration Zoom/Teams"],
        price: "Gratuit à 20€/mois"
      }
    ]
  },
  {
    title: "Création visuelle",
    color: "pink",
    tools: [
      {
        name: "Midjourney",
        description: "Génération d'images artistiques de haute qualité.",
        strengths: ["Qualité artistique exceptionnelle", "Styles variés", "Communauté active"],
        price: "10-60$/mois"
      },
      {
        name: "DALL-E 3 (OpenAI)",
        description: "Génération d'images réalistes intégrée à ChatGPT.",
        strengths: ["Réalisme", "Intégré à ChatGPT", "Compréhension fine des prompts"],
        price: "Inclus dans ChatGPT Plus"
      },
      {
        name: "Canva AI",
        description: "Design assisté par IA pour non-designers.",
        strengths: ["Interface intuitive", "Templates pro", "Génération et édition d'images"],
        price: "Gratuit à 12€/mois"
      }
    ]
  },
  {
    title: "Développement & Code",
    color: "green",
    tools: [
      {
        name: "GitHub Copilot",
        description: "Assistant de code qui complète et génère du code en temps réel.",
        strengths: ["Suggestions contextuelles", "Multi-langages", "Intégration IDE"],
        price: "10-19$/mois"
      },
      {
        name: "Cursor",
        description: "IDE optimisé IA avec intégration de plusieurs modèles LLM.",
        strengths: ["Chat avec le code", "Édition multi-fichiers", "Modèles au choix"],
        price: "20$/mois"
      },
      {
        name: "Replit AI",
        description: "Environnement de développement cloud avec assistant IA.",
        strengths: ["Tout-en-un", "Déploiement facile", "Collaboration temps réel"],
        price: "Gratuit à 20$/mois"
      }
    ]
  },
  {
    title: "Recherche & Analyse",
    color: "amber",
    tools: [
      {
        name: "Perplexity",
        description: "Moteur de recherche IA avec sources citées.",
        strengths: ["Sources vérifiables", "Réponses structurées", "Recherche approfondie"],
        price: "Gratuit à 20$/mois"
      },
      {
        name: "Julius",
        description: "Analyste de données IA. Upload vos fichiers, posez vos questions.",
        strengths: ["Analyse de données sans code", "Visualisations automatiques", "Excel/CSV/SQL"],
        price: "Gratuit à 20$/mois"
      }
    ]
  }
];

const OutilsIAEntreprisePage = () => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>30+ outils IA pour entreprise en 2026 | Formation Intelligence Artificielle</title>
        <meta name="description" content="Guide complet des meilleurs outils IA pour entreprise : ChatGPT, Claude, Midjourney, Copilot... Comparatif et formation professionnelle." />
        <meta name="keywords" content="outils IA entreprise, ChatGPT entreprise, formation IA, Microsoft Copilot, Midjourney formation" />
        <link rel="canonical" href="https://academie-ia.fr/outils-ia-entreprise" />
        <meta property="og:title" content="30+ outils IA pour entreprise en 2026" />
        <meta property="og:description" content="Guide complet et formation aux meilleurs outils IA professionnels." />
      </Helmet>

      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-full text-purple-400 text-sm font-medium mb-6">
              Comparatif complet 2026
            </span>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6">
              30+ outils IA pour{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                votre entreprise
              </span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              78% des entreprises utilisent déjà l'IA. Voici les outils qui font vraiment la différence — 
              et comment nous vous formons à les maîtriser.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/tunnel"
                className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-xl"
              >
                <Zap className="w-5 h-5" />
                Demander une formation
              </Link>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-8 py-4 bg-green-500 text-white font-bold rounded-xl"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp direct
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      {categories.map((category, catIndex) => (
        <section 
          key={category.title}
          className={`py-16 px-4 ${catIndex % 2 === 0 ? 'bg-gradient-to-b from-gray-900/50 to-[#050505]' : ''}`}
        >
          <div className="max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`text-2xl md:text-3xl font-bold text-white mb-8 flex items-center gap-3`}
            >
              <span className={`w-3 h-3 rounded-full bg-${category.color}-500`}></span>
              {category.title}
            </motion.h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.tools.map((tool, i) => (
                <motion.div
                  key={tool.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-${category.color}-500/30 transition-all`}
                >
                  <h3 className="text-xl font-bold text-white mb-2">{tool.name}</h3>
                  <p className="text-gray-400 text-sm mb-4">{tool.description}</p>
                  
                  <ul className="space-y-2 mb-4">
                    {tool.strengths.map((strength, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-300">
                        <CheckCircle className={`w-4 h-4 text-${category.color}-400 flex-shrink-0 mt-0.5`} />
                        {strength}
                      </li>
                    ))}
                  </ul>
                  
                  <div className={`text-sm font-medium text-${category.color}-400`}>
                    {tool.price}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Stats */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-900/20 to-pink-900/20 border-y border-white/10">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { stat: "78%", desc: "des entreprises utilisent l'IA" },
              { stat: "+45%", desc: "de productivité d'ici 2030 (McKinsey)" },
              { stat: "10h", desc: "gagnées par mois avec Copilot" }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-2">
                  {item.stat}
                </div>
                <p className="text-gray-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ne vous contentez pas de connaître ces outils.
              <br />
              <span className="text-purple-400">Maîtrisez-les.</span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Notre formation vous fait passer de "j'ai testé ChatGPT" à "j'automatise mon travail avec l'IA".
            </p>
            <Link
              to="/formation-ia"
              className="inline-flex items-center gap-2 px-10 py-5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xl font-bold rounded-2xl"
            >
              <Zap className="w-6 h-6" />
              Découvrir nos formations
              <ArrowRight className="w-6 h-6" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center border-t border-white/10">
        <p className="text-gray-500 text-sm">
          Académie IA © {new Date().getFullYear()} — Formation Intelligence Artificielle
        </p>
      </footer>
    </div>
  );
};

export default OutilsIAEntreprisePage;
